import React, { useState } from "react";
import { NumberField } from "@base-ui/react";
import {
  FormControl,
  FormHelperText,
  InputLabel,
  styled,
  useTheme,
} from "@mui/material";
import { Controller } from "react-hook-form";

// --- STYLES ---
// Note: We destructured $error, $focused, $hidesteppers (with $)
const StyledNumberField = styled(NumberField.Root)(
  ({ theme, error, focused, hidesteppers }) => ({
    fontFamily: theme.typography.fontFamily,
    display: "flex",
    flexDirection: "column",
    width: "100%",
    position: "relative",

    "& .input-group": {
      display: "flex",
      alignItems: "center",
      position: "relative",
      borderRadius: "4px",

      // Use $ props here
      border: `1px solid ${
        error
          ? theme.palette.error.main
          : focused
            ? theme.palette.primary.main
            : "rgba(0, 0, 0, 0.23)"
      }`,

      borderWidth: focused ? "2px" : "1px",
      padding: focused ? "0 13px" : "0 14px",
      transition:
        "border-color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms, border-width 200ms",

      "&:hover": {
        borderColor: error
          ? theme.palette.error.main
          : focused
            ? theme.palette.primary.main
            : theme.palette.text.primary,
      },
    },

    "& input": {
      all: "unset",
      width: "100%",
      height: "1.4375em",
      padding: "16.5px 0",
      color: theme.palette.text.primary,
      fontSize: "1rem",
      textAlign: "left",
      appearance: "textfield",
      "&::-webkit-inner-spin-button, &::-webkit-outer-spin-button": {
        appearance: "none",
        margin: 0,
      },
    },

    // ARROWS LOGIC
    "& .btn-group": {
      // FIX: Actually use the prop to hide/show
      display: hidesteppers ? "none" : "flex",
      flexDirection: "column",
      marginLeft: "8px",
      justifyContent: "center",
      height: "100%",
      gap: "2px",
    },
    "& button": {
      all: "unset",
      cursor: "pointer",
      fontSize: "10px",
      lineHeight: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "2px",
      color: theme.palette.text.secondary,
      userSelect: "none",
      "&:hover": {
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.action.hover,
        borderRadius: "2px",
      },
    },
  }),
);

const FormNumberInput = ({
  name,
  control,
  label,
  rules = {},
  min = 0,
  max,
  step = 1,
  minLength,
  maxLength,
  hidesteppers = false,
  disableGrouping = false,
  ...props
}) => {
  const theme = useTheme();
  const [focused, setFocused] = useState(false);

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        ...rules,
        // Numerical constraints
        min: { value: min, message: `Minimum value is ${min}` },
        max: max
          ? { value: max, message: `Maximum value is ${max}` }
          : undefined,

        // FIX: RHF minLength/maxLength do not work on Numbers.
        // We must use 'validate' to check string length.
        validate: {
          ...rules.validate, // Keep existing validations if any
          checkMinLength: (val) => {
            if (!minLength || val === null || val === undefined) return true;
            return (
              String(val).length >= minLength ||
              `Must be at least ${minLength} digits`
            );
          },
          checkMaxLength: (val) => {
            if (!maxLength || val === null || val === undefined) return true;
            return (
              String(val).length <= maxLength ||
              `Must be at most ${maxLength} digits`
            );
          },
        },
      }}
      render={({
        field: { onChange, value, ref, onBlur },
        fieldState: { error },
      }) => {
        // Handle Controlled State
        const controlledValue =
          value === "" || value === undefined ? null : value;

        const isShrunk = focused || controlledValue !== null;

        return (
          <FormControl
            fullWidth
            error={!!error}
            // sx={{ mt: 2 }}
            variant="outlined"
          >
            <InputLabel
              htmlFor={`${name}-input`}
              shrink={isShrunk}
              sx={{
                backgroundColor: theme.palette.background.paper,
                padding: "0 4px",
                marginLeft: "-4px",
              }}
            >
              {label}
            </InputLabel>

            <StyledNumberField
              id={`${name}-field`}
              min={min}
              max={max}
              step={step}
              format={{
                useGrouping: !disableGrouping,
                maximumFractionDigits: 2,
                minimumFractionDigits: 0,
              }}
              value={controlledValue}
              onValueChange={(val) => onChange(val)}
              // FIX: Use $ prefix for custom props
              error={!!error}
              focused={focused}
              hidesteppers={hidesteppers}
              {...props}
            >
              <div className="input-group">
                <NumberField.Input
                  id={`${name}-input`}
                  ref={ref}
                  onFocus={() => setFocused(true)}
                  onBlur={(e) => {
                    setFocused(false);
                    onBlur(e);
                  }}
                  placeholder={focused ? "" : ""} // OTP usually doesn't need placeholder
                  onKeyDown={(e) => {
                    // Block non-numeric keys often used in math
                    if (["-", "e", "E", "+"].includes(e.key))
                      e.preventDefault();
                  }}
                />
                <NumberField.Group className="btn-group">
                  <NumberField.Increment title="Increase">
                    ▲
                  </NumberField.Increment>
                  <NumberField.Decrement title="Decrease">
                    ▼
                  </NumberField.Decrement>
                </NumberField.Group>
              </div>
            </StyledNumberField>

            {error && <FormHelperText>{error.message}</FormHelperText>}
          </FormControl>
        );
      }}
    />
  );
};

export default FormNumberInput;
